import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

type DateRangeValidationUnit = 'month' | 'day' | 'week' | 'year';

class TimeService {
    sortDates(dateA: string, dateB: string, order: 'asc' | 'desc' = 'desc') {
        if (!dateA || !dateB) {
            return 0;
        }

        const a = dayjs(dateA);
        const b = dayjs(dateB);

        if (a.valueOf() === b.valueOf()) {
            return 0;
        }

        if (order === 'asc') {
            return a.valueOf() < b.valueOf() ? -1 : 1;
        } else {
            return b.valueOf() < a.valueOf() ? -1 : 1;
        }
    }

    isWithinWindow(date: string, releaseWindow: number = 30): boolean {
        const releaseDate = new Date(date);
        const today = new Date();
        const endDate = new Date(releaseDate.getTime() + releaseWindow * 24 * 60 * 60 * 1000);
        return today <= endDate;
    }

    /**
     * Checks if a date range exceeds a specified limit
     * @param startDate - Start date string
     * @param endDate - End date string
     * @param maxValue - Maximum allowed value
     * @param unit - Unit of measurement ('month', 'day', 'week', 'year')
     * @returns true if the range exceeds the limit, false otherwise
     */
    isDateRangeExceeding(
        startDate: string,
        endDate: string,
        maxValue: number,
        unit: DateRangeValidationUnit = 'month'
    ): boolean {
        if (!startDate || !endDate) {
            return false;
        }

        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const diff = end.diff(start, unit, true);

        return diff > maxValue;
    }

    formatDateWithTimezone(
        dateToFormat: string | null | undefined,
        userTimezone: string
    ): string | null {
        if (!dateToFormat) {
            return null;
        }

        return dayjs.tz(dateToFormat, userTimezone).toISOString();
    }

    /**
     * Check if a date string is empty or nil
     */
    private isEmptyDate(dateStr: string | undefined | null): boolean {
        return !dateStr || dateStr === '';
    }

    /**
     * Format a date string to ISO 8601 format with timezone
     * Used for group API calls
     * @param dateStr - The date string to format
     * @param timezone - The timezone to apply
     * @returns Formatted ISO string or undefined if dateStr is empty/nil
     */
    formatDateForGroup(dateStr: string | undefined | null, timezone: string): string | undefined {
        if (this.isEmptyDate(dateStr) || !timezone) return undefined;
        return dayjs.tz(dateStr, timezone).toISOString();
    }

    /**
     * Format a date string to short date format (YYYY-MM-DD)
     * Used for API calls that expect date-only format
     * @param dateStr - The date string to format
     * @returns Formatted date string (YYYY-MM-DD) or undefined if dateStr is empty/nil
     */
    formatDateForAPI(dateStr: string | undefined | null): string | undefined {
        if (this.isEmptyDate(dateStr)) return undefined;
        return dateStr.slice(0, 10);
    }

    /**
     * Format a date string to ISO 8601 UTC format for Analytics V2 API
     * Converts YYYY-MM-DD to YYYY-MM-DDTHH:mm:ss.SSSZ (UTC)
     * If date is already in ISO format (contains 'T'), returns it as-is
     * @param dateStr - The date string to format (YYYY-MM-DD or ISO)
     * @returns Formatted ISO UTC string or undefined if dateStr is empty/nil
     */
    formatDateForAnalyticsV2(dateStr: string | undefined | null, endDate: boolean = false): string | undefined {
        if (this.isEmptyDate(dateStr)) return undefined;
        if (dateStr.includes('T')) return dateStr;
        if (endDate) {
            // Add 23:59:59.999 to the end of the day
            return dayjs.utc(dateStr).endOf('day').toISOString();
        }
        // Add 00:00:00.000 to the start of the day
        return dayjs.utc(dateStr).startOf('day').toISOString();
    }
}

const timeService = new TimeService();

export { timeService as TimeService };
